import { useCallback, useEffect, useRef, useState } from "react";
import { haptics } from "@/lib/haptics";

/**
 * Custom pull-to-refresh for the mobile web / Capacitor build. Nothing
 * pre-built in the stack does this, so we ride pointer events.
 *
 * Contract:
 *   - Attach `onPointerDown` (and, on iOS, `onTouchStart`) to whichever
 *     element owns the scroll — usually the scrolling `<main>` or a
 *     specific scroll container.
 *   - The hook only engages when `scrollTop === 0` and the pointer moves
 *     down. Any upward motion cancels the gesture.
 *   - Pulled distance is `progress` (0..1 up to threshold, then >1 as the
 *     rubber-band continues past). At `progress ≥ 1` on release we fire
 *     `onRefresh()` and a haptic tick; otherwise the state resets.
 *   - The user's own scroll never triggers refresh — we `preventDefault`
 *     only while actively pulling, and only when the touch started at
 *     scrollTop === 0.
 *
 * The pull indicator is rendered by <PullToRefresh> (components/ui) —
 * this hook keeps only the mechanics and the haptic ticking as the
 * threshold is crossed.
 */
export interface UsePullToRefreshOptions {
  onRefresh: () => Promise<unknown> | void;
  /** Distance in pixels that maps to progress = 1. Default 80. */
  threshold?: number;
  /** Whether the gesture is enabled at all (e.g., disabled while a modal is open). */
  enabled?: boolean;
}

export interface PullToRefreshState {
  /** Current pull distance in pixels (>= 0). */
  distance: number;
  /** distance / threshold. Reaches 1 at threshold; keeps growing past. */
  progress: number;
  /** True while the async refresh callback is running. */
  refreshing: boolean;
}

export function usePullToRefresh(opts: UsePullToRefreshOptions) {
  const { onRefresh, threshold = 80, enabled = true } = opts;
  const [state, setState] = useState<PullToRefreshState>({
    distance: 0,
    progress: 0,
    refreshing: false,
  });

  // Tracked per-gesture. We keep them in refs so callbacks don't need
  // to re-run when the pull state re-renders.
  const startY = useRef<number | null>(null);
  const active = useRef(false);
  const armed = useRef(false); // whether we already fired the arm-haptic

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
      if (!enabled) return;
      // Only track finger/pen — mouse users don't pull to refresh.
      if ("pointerType" in e && e.pointerType === "mouse") return;
      // Only engage at the very top of the scroll.
      const scroller = e.currentTarget as HTMLElement;
      const doc = document.scrollingElement;
      const scrollTop = doc?.scrollTop ?? scroller.scrollTop ?? 0;
      if (scrollTop > 0) return;

      const y = "touches" in e ? e.touches[0]?.clientY ?? 0 : (e as React.PointerEvent).clientY;
      startY.current = y;
      active.current = true;
      armed.current = false;
    },
    [enabled],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent | TouchEvent) => {
      if (!enabled || !active.current || startY.current == null) return;
      const y =
        "touches" in e ? e.touches[0]?.clientY ?? 0 : (e as PointerEvent).clientY;
      const raw = y - startY.current;
      if (raw <= 0) {
        // Any upward motion cancels the gesture — the user is scrolling
        // up instead of pulling down.
        setState((s) => (s.distance === 0 ? s : { ...s, distance: 0, progress: 0 }));
        return;
      }

      // Rubber-band: past the threshold we grow logarithmically so the
      // pull "resists" further motion instead of stretching linearly.
      const eff =
        raw <= threshold ? raw : threshold + Math.log(raw - threshold + 1) * 24;
      const progress = eff / threshold;

      if (progress >= 1 && !armed.current) {
        armed.current = true;
        haptics.select();
      }

      // Suppress the browser's own scroll while pulling — only while the
      // touch started at the top, so normal scrolling on the same
      // element isn't blocked.
      if (e.cancelable) e.preventDefault();
      setState((s) => ({ ...s, distance: eff, progress }));
    },
    [enabled, threshold],
  );

  const onPointerEnd = useCallback(async () => {
    if (!active.current) return;
    active.current = false;
    startY.current = null;
    const crossed = armed.current;
    armed.current = false;

    if (crossed) {
      setState((s) => ({ ...s, refreshing: true, distance: threshold, progress: 1 }));
      try {
        await onRefresh();
      } finally {
        setState({ distance: 0, progress: 0, refreshing: false });
      }
    } else {
      setState({ distance: 0, progress: 0, refreshing: false });
    }
  }, [onRefresh, threshold]);

  // Wire the move/up listeners at the window level with { passive: false }
  // so preventDefault actually blocks the browser's overscroll. React's
  // own onTouchMove is passive by default, hence the escape hatch.
  useEffect(() => {
    if (!enabled) return;
    const onMove = (e: TouchEvent | PointerEvent) => onPointerMove(e);
    const onUp = () => void onPointerEnd();
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [enabled, onPointerMove, onPointerEnd]);

  return {
    state,
    /** Spread on the scroll container to arm the gesture. */
    bind: {
      onPointerDown: onPointerDown as unknown as React.PointerEventHandler<HTMLElement>,
      onTouchStart: onPointerDown as unknown as React.TouchEventHandler<HTMLElement>,
    },
  };
}
